<template>
  <div class="social-wall-composer">
    <form :aria-label="$t('general.share_post')"
          class="interactive-social-wall__composer"
          @submit.prevent="submitPostDraft">
      <div :class="{
          'interactive-social-wall__compose-box--submitting': submitting,
        }"
           class="interactive-social-wall__compose-box"
      >
        <textarea ref="postInput" v-model="postDraft"
                  :disabled="submitting"
                  :placeholder="placeholder || $t('general.share_a')"
                  class="interactive-social-wall__compose-input"
                  rows="2"
                  @click="rememberPostTextSelection"
                  @input="handlePostDraftInput"
                  @keydown.ctrl.enter.prevent="submitPostDraft"
                  @keydown.meta.enter.prevent="submitPostDraft"
                  @keyup="rememberPostTextSelection"
                  @select="rememberPostTextSelection"/>

        <div v-if="selectedMedia.length" :aria-label="$t('general.selected_media_files')"
             class="interactive-social-wall__compose-media-list">
          <article v-for="media in selectedMedia"
                   :key="media.id"
                   :title="media.file.name"
                   class="interactive-social-wall__compose-media-item">
            <img v-if="media.kind === 'image'"
                 :alt="media.file.name"
                 :src="media.previewUrl"/>
            <video v-else
                   :aria-label="media.file.name"
                   :src="media.previewUrl"
                   muted
                   playsinline
                   preload="metadata"/>
            <span>
              <i :class="media.kind === 'video' ? 'uil-video' : 'uil-image'"
                 aria-hidden="true"
              />
              {{ media.file.name }}
            </span>
            <button :aria-label="$t('general.remove_file', { name: media.file.name })"
                    :disabled="submitting"
                    type="button"
                    @click="removeSelectedMedia(media.id)">
              <i aria-hidden="true" class="uil-times"/>
            </button>
          </article>
        </div>

        <p v-if="visibleErrorMessage"
           class="interactive-social-wall__compose-error"
           role="alert">
          <i aria-hidden="true" class="uil-exclamation-circle"/>
          {{ visibleErrorMessage }}
        </p>

        <div class="interactive-social-wall__compose-toolbar">
          <div class="interactive-social-wall__compose-actions">
            <WallEmojiPicker compact
                             class="interactive-social-wall__compose-emoji-picker"
                             :disabled="submitting"
                             @open="rememberPostTextSelection"
                             @select="insertPostEmoji"
            />
            <input ref="composerMediaInput"
                   :disabled="submitting"
                   accept="image/*,video/*"
                   class="interactive-social-wall__compose-file-input"
                   multiple
                   tabindex="-1"
                   type="file"
                   @change="handleMediaSelection"/>
            <button :aria-label="
                selectedMedia.length
                  ? $t('general.add_another_image_or_video')
                  : $t('general.add_image_or_video')
              "
                    :disabled="submitting"
                    class="interactive-social-wall__compose-media-button"
                    type="button"
                    @click="openMediaPicker"
            >
              <span aria-hidden="true" class="interactive-social-wall__compose-media-button-icon">
                <i class="uil-images"/>
              </span>
              <span class="interactive-social-wall__compose-media-button-copy">
                <strong>{{ $t('general.add_media') }}</strong>
                <small>
                  {{
                    selectedMedia.length
                        ? $t('general.selected_file_count', { count: selectedMedia.length })
                        : $t('general.image_or_video')
                  }}
                </small>
              </span>
              <span v-if="selectedMedia.length"
                    aria-hidden="true"
                    class="interactive-social-wall__compose-media-count"
              >
                {{ selectedMedia.length }}
              </span>
            </button>
          </div>

          <div class="interactive-social-wall__compose-submit-area">
            <button :aria-busy="submitting"
                    :disabled="!canSubmitPost"
                    :aria-label="$t('general.share')"
                    class="interactive-social-wall__send"
                    type="submit"
            >
              <span v-if="submitting"
                    aria-hidden="true"
                    class="interactive-social-wall__inline-spinner interactive-social-wall__inline-spinner--light"
              />
              <i v-else aria-hidden="true" class="uil-navigator"/>
              <span>{{ submitting ? $t('general.sharing') : $t('general.share') }}</span>
            </button>
          </div>
        </div>
      </div>
    </form>

    <SocialWallImageRatioEditor ref="imageRatioEditor"
                                @error="mediaSelectionError = $event"
                                @file-ready="addCroppedImage"
                                @processing-change="isImageEditorProcessing = $event"
    />
  </div>
</template>

<script>
import WallEmojiPicker from "@/view/User/Wall/components/WallEmojiPicker.vue";
import SocialWallImageRatioEditor from "./SocialWallImageRatioEditor.vue";

const { insertTextAtSelection } = require("@/utils/emojiUtils");

export default {
  name: "SocialWallComposer",
  components: {
    SocialWallImageRatioEditor,
    WallEmojiPicker,
  },
  props: {
    placeholder: {
      type: String,
      default: "",
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    submitError: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      postDraft: "",
      selectedMedia: [],
      mediaItemSequence: 0,
      isImageEditorProcessing: false,
      mediaSelectionError: "",
      postTextSelectionStart: 0,
      postTextSelectionEnd: 0,
    };
  },
  computed: {
    canSubmitPost() {
      return (
          this.postDraft.trim().length >= 3 &&
          !this.submitting &&
          !this.isImageEditorProcessing
      );
    },
    visibleErrorMessage() {
      return this.mediaSelectionError || this.submitError;
    },
  },
  beforeDestroy() {
    this.clearSelectedMedia();
  },
  methods: {
    handlePostDraftInput() {
      this.rememberPostTextSelection();
      this.clearComposerError();
    },
    rememberPostTextSelection() {
      const textarea = this.$refs.postInput;
      const textLength = String(this.postDraft || "").length;

      if (!textarea) {
        this.postTextSelectionStart = textLength;
        this.postTextSelectionEnd = textLength;
        return;
      }

      this.postTextSelectionStart = Number.isInteger(textarea.selectionStart)
          ? textarea.selectionStart
          : textLength;
      this.postTextSelectionEnd = Number.isInteger(textarea.selectionEnd)
          ? textarea.selectionEnd
          : this.postTextSelectionStart;
    },
    insertPostEmoji(emoji) {
      if (!emoji || this.submitting) return;

      const insertion = insertTextAtSelection(
          this.postDraft,
          emoji,
          this.postTextSelectionStart,
          this.postTextSelectionEnd
      );

      this.postDraft = insertion.text;
      this.postTextSelectionStart = insertion.cursor;
      this.postTextSelectionEnd = insertion.cursor;
      this.clearComposerError();

      this.$nextTick(() => {
        const textarea = this.$refs.postInput;
        if (!textarea) return;

        textarea.focus();
        textarea.setSelectionRange(insertion.cursor, insertion.cursor);
      });
    },
    clearComposerError() {
      this.mediaSelectionError = "";
      if (this.submitError) this.$emit("clear-error");
    },
    focusPostInput() {
      this.$nextTick(() => this.$refs.postInput?.focus());
    },
    resetComposer() {
      this.postDraft = "";
      this.postTextSelectionStart = 0;
      this.postTextSelectionEnd = 0;
      this.mediaSelectionError = "";
      this.clearSelectedMedia();
    },
    submitPostDraft() {
      if (this.submitting) return;
      if (
          this.isImageEditorProcessing ||
          this.$refs.imageRatioEditor?.isEditingImages()
      ) {
        this.mediaSelectionError = this.$t('general.complete_image_ratio_selection');
        return;
      }

      const text = this.postDraft.trim();
      if (text.length < 3) {
        this.focusPostInput();
        return;
      }

      this.mediaSelectionError = "";
      this.$emit("submit", {
        text,
        files: this.selectedMedia.map((media) => media.file),
      });
    },
    getMediaKind(file) {
      const type = file?.type || "";
      if (type.startsWith("image/")) return "image";
      if (type.startsWith("video/")) return "video";
      return "";
    },
    getFileSignature(file) {
      return [file?.name, file?.size, file?.lastModified].join("-");
    },
    getSelectedFileSignatures() {
      const signatures = new Set(
          this.selectedMedia.map(
              (media) => media.sourceSignature || this.getFileSignature(media.file)
          )
      );

      (this.$refs.imageRatioEditor?.getPendingSourceFiles() || []).forEach(
          (file) => {
            signatures.add(this.getFileSignature(file));
          }
      );
      return signatures;
    },
    createMediaPreviewUrl(file) {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      return urlApi?.createObjectURL ? urlApi.createObjectURL(file) : "";
    },
    revokeMediaPreviewUrl(previewUrl) {
      const urlApi = typeof window !== "undefined" ? window.URL : null;
      if (previewUrl && urlApi?.revokeObjectURL) {
        urlApi.revokeObjectURL(previewUrl);
      }
    },
    addSelectedMedia(file, kind, sourceFile = file) {
      const sourceSignature = this.getFileSignature(sourceFile);
      const alreadyAdded = this.selectedMedia.some(
          (media) =>
              (media.sourceSignature || this.getFileSignature(media.file)) ===
              sourceSignature
      );
      if (alreadyAdded) return true;

      const previewUrl = this.createMediaPreviewUrl(file);
      if (!previewUrl) return false;

      this.mediaItemSequence += 1;
      this.selectedMedia = [
        ...this.selectedMedia,
        {
          id: `composer-media-${this.mediaItemSequence}`,
          file,
          kind,
          previewUrl,
          sourceSignature,
        },
      ];
      return true;
    },
    addCroppedImage({ file, sourceFile } = {}) {
      if (!file) return;
      if (!this.addSelectedMedia(file, "image", sourceFile || file)) {
        this.mediaSelectionError = this.$t('general.image_preview_failed');
      }
    },
    openMediaPicker() {
      if (this.submitting) return;
      this.$refs.composerMediaInput?.click();
    },
    handleMediaSelection(event) {
      if (this.submitting) return;
      const inputFiles = Array.from(event?.target?.files || []);
      if (!inputFiles.length) return;

      const existingSignatures = this.getSelectedFileSignatures();
      const invalidFiles = [];
      const selectedImages = [];
      inputFiles.forEach((file) => {
        const kind = this.getMediaKind(file);
        if (!kind) {
          invalidFiles.push(file);
          return;
        }

        const signature = this.getFileSignature(file);
        if (existingSignatures.has(signature)) return;
        existingSignatures.add(signature);
        if (kind === "image") {
          selectedImages.push(file);
          return;
        }
        if (!this.addSelectedMedia(file, kind)) invalidFiles.push(file);
      });

      this.mediaSelectionError = invalidFiles.length
          ? this.$t('general.image_or_video_only')
          : "";
      if (event?.target) event.target.value = "";
      this.$refs.imageRatioEditor?.enqueueImages(selectedImages);
    },
    removeSelectedMedia(mediaId) {
      if (this.submitting) return;
      const removedMedia = this.selectedMedia.find(
          (media) => media.id === mediaId
      );
      if (!removedMedia) return;
      this.selectedMedia = this.selectedMedia.filter(
          (media) => media.id !== mediaId
      );
      this.revokeMediaPreviewUrl(removedMedia?.previewUrl);
    },
    clearSelectedMedia() {
      this.selectedMedia.forEach((media) => {
        this.revokeMediaPreviewUrl(media.previewUrl);
      });
      this.selectedMedia = [];
      this.isImageEditorProcessing = false;
      this.$refs.imageRatioEditor?.resetEditor();
      if (this.$refs?.composerMediaInput) {
        this.$refs.composerMediaInput.value = "";
      }
    },
  },
};
</script>

<style scoped>
.social-wall-composer {
  position: relative;
  z-index: 30;
}

.social-wall-composer,
.social-wall-composer * {
  box-sizing: border-box;
}

.social-wall-composer button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 0;
  outline: none;
  box-shadow: none;
}

.social-wall-composer button:disabled {
  cursor: not-allowed;
}

.interactive-social-wall__composer {
  display: block;
  margin: 14px 14px 0;
  padding-bottom: 13px;
  border-bottom: 1px solid #e5e9ef;
}

.interactive-social-wall__compose-box {
  width: 100%;
  min-width: 0;
  overflow: visible;
  background: #ffffff;
  background-clip: padding-box;
  border: 1px solid #dbe2eb;
  border-radius: 6px;
  transition: border-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
}

.interactive-social-wall__compose-box:focus-within {
  border-color: #72c861;
  box-shadow: 0 0 0 3px rgba(80, 184, 62, 0.1);
}

.interactive-social-wall__compose-box--submitting {
  opacity: 0.72;
}

.interactive-social-wall__compose-input {
  display: block;
  width: 100%;
  height: 68px;
  min-height: 68px;
  max-height: 68px;
  margin: 0 !important;
  padding: 10px 11px;
  overflow-y: auto;
  color: #202734;
  font-family: var(--font-primary, "Poppins", sans-serif);
  font-size: 11.5px;
  font-weight: 400;
  line-height: 1.5;
  resize: none;
  background: #ffffff;
  border: 0;
  border-bottom: 1px solid #dbe2eb;
  border-radius: 5px 5px 0 0;
  outline: none;
}

.interactive-social-wall__compose-input::placeholder {
  color: #7a8497;
}

.interactive-social-wall__compose-input:disabled {
  cursor: wait;
  background: #f8fafb;
}

.interactive-social-wall__compose-media-list {
  display: flex;
  gap: 7px;
  padding: 8px 10px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  border-bottom: 1px solid #dbe2eb;
  scrollbar-color: #ccd3dd transparent;
  scrollbar-width: thin;
  scroll-snap-type: x proximity;
}

.interactive-social-wall__compose-media-item {
  position: relative;
  flex: 0 0 104px;
  width: 104px;
  height: 68px;
  overflow: hidden;
  background: #edf1f5;
  border: 1px solid #dde4eb;
  border-radius: 6px;
  scroll-snap-align: start;
}

.interactive-social-wall__compose-media-item img,
.interactive-social-wall__compose-media-item video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.interactive-social-wall__compose-media-item > span {
  --iconColor: #ffffff;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  padding: 12px 5px 4px;
  overflow: hidden;
  color: #ffffff;
  font-size: 7px;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
  pointer-events: none;
  background: linear-gradient(transparent, rgba(17, 24, 39, 0.82));
}

.interactive-social-wall__compose-media-item > span i {
  flex: 0 0 auto;
  font-size: 9px;
}

.interactive-social-wall__compose-media-item > button {
  --iconColor: #ffffff;
  position: absolute;
  top: 4px;
  right: 4px;
  width: 21px;
  height: 21px;
  color: #ffffff !important;
  background: rgba(17, 24, 39, 0.75) !important;
  border-radius: 50%;
}

.interactive-social-wall__compose-media-item > button i {
  font-size: 12px;
}

.interactive-social-wall__compose-error {
  --iconColor: #d84f4f;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 0;
  padding: 7px 10px;
  color: #c74444;
  font-size: 8.5px;
  line-height: 1.4;
  background: #fff7f7;
  border-bottom: 1px solid #f2dada;
}

.interactive-social-wall__compose-error i {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 11px;
}

.interactive-social-wall__compose-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  min-height: 54px;
  padding: 7px 9px;
  background: #fbfcfd;
  border-radius: 0 0 5px 5px;
}

.interactive-social-wall__compose-actions {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.interactive-social-wall__compose-emoji-picker {
  flex: 0 0 auto;
  margin-right: auto;
}

.interactive-social-wall__compose-media-button {
  display: flex !important;
  min-width: 0;
  min-height: 38px;
  gap: 8px;
  padding: 4px 8px 4px 5px !important;
  color: #59667a !important;
  text-align: left;
  border: 1px solid transparent !important;
  border-radius: 7px;
  transition: color 150ms ease, background-color 150ms ease,
  border-color 150ms ease;
}

.interactive-social-wall__compose-media-button:hover,
.interactive-social-wall__compose-media-button:focus-visible {
  color: #3f9830 !important;
  background: #f1f8ef !important;
  border-color: #d9ebd5 !important;
}

.interactive-social-wall__compose-media-button:disabled {
  opacity: 0.5;
}

.interactive-social-wall__compose-media-button-icon {
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: var(--iconColor);
  background: #edf8ea;
  border-radius: 7px;
}

.interactive-social-wall__compose-media-button-icon i {
  font-size: 16px;
}

.interactive-social-wall__compose-media-button-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.2;
}

.interactive-social-wall__compose-media-button-copy strong {
  overflow: hidden;
  color: #4b5669;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__compose-media-button-copy small {
  overflow: hidden;
  color: #8a94a4;
  font-size: 7.5px;
  font-weight: 400;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.interactive-social-wall__compose-media-count {
  display: inline-flex;
  flex: 0 0 19px;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 19px;
  margin-left: 2px;
  padding: 0 4px;
  color: #ffffff;
  font-size: 7.5px;
  font-weight: 700;
  line-height: 1;
  background: #50b83e;
  border-radius: 10px;
}

.interactive-social-wall__compose-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  clip-path: inset(50%);
}

.interactive-social-wall__compose-submit-area {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.interactive-social-wall__send {
  min-width: 76px;
  height: 36px;
  gap: 6px;
  padding: 0 12px !important;
  color: #ffffff !important;
  font-size: 9.5px !important;
  font-weight: 600 !important;
  background: var(--iconColor) !important;
  border: 1px solid var(--iconColor) !important;
  border-radius: 7px;
  box-shadow: 0 4px 10px color-mix(in srgb, var(--iconColor) 22%, transparent) !important;
  transition: transform 150ms ease, background-color 150ms ease,
  box-shadow 150ms ease;
}

.interactive-social-wall__send:not(:disabled):hover,
.interactive-social-wall__send:not(:disabled):focus-visible {
  background: color-mix(in srgb, var(--iconColor) 82%, #000000) !important;
  border-color: color-mix(in srgb, var(--iconColor) 82%, #000000) !important;
  box-shadow: 0 5px 13px color-mix(in srgb, var(--iconColor) 30%, transparent) !important;
  transform: translateY(-1px);
}

.interactive-social-wall__send i {
  color: #ffffff !important;
  font-size: 16px;
  transform: rotate(-8deg);
}

.interactive-social-wall__send .interactive-social-wall__inline-spinner {
  margin-right: 0;
}

.interactive-social-wall__send:disabled {
  --iconColor: #9ca6b4;
  color: #9ca6b4 !important;
  background: #f3f5f7 !important;
  border-color: #e5e9ee !important;
  box-shadow: none !important;
}

.interactive-social-wall__send:disabled i {
  color: #9ca6b4 !important;
}

.interactive-social-wall__inline-spinner {
  display: inline-block;
  width: 11px;
  height: 11px;
  border: 1.5px solid rgba(104, 117, 139, 0.32);
  border-top-color: #50b83e;
  border-radius: 50%;
  animation: interactive-social-wall-spin 700ms linear infinite;
}

.interactive-social-wall__inline-spinner--light {
  margin-right: 6px;
  border-color: rgba(255, 255, 255, 0.42);
  border-top-color: #ffffff;
}

@keyframes interactive-social-wall-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .interactive-social-wall__compose-toolbar {
    gap: 7px;
    padding: 7px;
  }

  .interactive-social-wall__send {
    min-width: 72px;
    padding-right: 10px !important;
    padding-left: 10px !important;
  }

  .interactive-social-wall__compose-input {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__composer {
    margin-right: 12px !important;
    margin-left: 12px !important;
  }

  .interactive-social-wall__compose-media-button {
    padding-right: 6px !important;
  }

  .interactive-social-wall__compose-media-button-copy strong {
    font-size: 8.5px;
  }
}
</style>
